
import { defineComponent, PropType, computed } from "vue";
import { GroupedAlert, MessageType, AlertType } from "@/stores/telegramAlerts";
import AlertDetails from "@/components/alerts/AlertDetails.vue";
import {
  formatDate,
  cleanAlertText,
  formatPrice,
  getAlertSummary,
  calculatePips,
  calculateRR,
  getRRColorClass,
} from "@/components/alerts/utils/alertFormatters";
import {
  getResultLabel,
  getResultBadgeClass,
} from "@/components/alerts/utils/alertStatus";
import {
  getOrderTypeLabel,
  getOrderTypeShortLabel,
  getOrderTypeBadgeClass,
} from "@/components/alerts/utils/orderTypeHelpers";

export default defineComponent({
  name: "ForexAlertRow",
  components: {
    AlertDetails,
  },
  props: {
    alert: {
      type: Object as PropType<GroupedAlert>,
      required: true,
    },
    rowNumber: {
      type: Number,
      required: true,
    },
    alertType: {
      type: String,
      required: true, // "forex" or "synthetic_indices"
    },
    expandedAlertId: {
      type: Object as PropType<Set<string>>,
      required: true,
    },
    editingAlertId: {
      type: String as PropType<string | null>,
      default: null,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    isReprocessing: {
      type: Object as PropType<Record<number, boolean>>,
      default: () => ({}),
    },
  },
  emits: [
    "toggle-expand",
    "start-edit",
    "cancel-edit",
    "save-edit",
    "reprocess-message",
  ],
  setup(props, { emit }) {
    const isExpanded = computed(() =>
      props.expandedAlertId.has(props.alert.alertId)
    );
    const isEditing = computed(
      () => props.editingAlertId === props.alert.alertId
    );

    const toggleExpand = () => {
      emit("toggle-expand", props.alert.alertId);
    };

    const hasEditedMessages = (alert: GroupedAlert) => {
      return alert.relatedMessages.some(
        (msg) =>
          msg.messageType === MessageType.UPDATE ||
          msg.messageType === MessageType.ADJUSTMENT
      );
    };

    const hasDeletedMessages = (alert: GroupedAlert) => {
      return alert.relatedMessages.some(
        (msg) => msg.messageType === MessageType.MANUAL_CLOSE
      );
    };

    // Helper to check if there's a pips calculation error (inconsistent trade data)
    const hasPipsError = (alert: GroupedAlert): boolean => {
      const msg = alert.mainMessage;
      if (!msg.entryPrice || !msg.stopLoss) return false;

      // Check if any TP causes an error
      if (msg.takeProfit && msg.takeProfit.length > 0) {
        const firstTpResult = calculatePips(
          msg.entryPrice,
          msg.takeProfit[0],
          msg.pair || msg.index,
          props.alertType,
          msg.direction,
          msg.stopLoss
        );
        return firstTpResult.includes("ERROR");
      }

      return false;
    };

    return {
      isExpanded,
      isEditing,
      toggleExpand,
      formatDate,
      cleanAlertText,
      formatPrice,
      calculatePips,
      calculateRR,
      getRRColorClass,
      getAlertSummary,
      getResultLabel,
      getResultBadgeClass,
      hasEditedMessages,
      hasDeletedMessages,
      hasPipsError,
      getOrderTypeLabel,
      getOrderTypeShortLabel,
      getOrderTypeBadgeClass,
      AlertType,
    };
  },
});
